import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Loading from 'assets/images/loading.png';
import { poppins400 } from 'theme/common.style';
import {
  ADMIN_COLORS,
  adminHelperText,
  adminSecondaryButton,
} from './Admin.style';

const wrap = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '48px 24px',
  textAlign: 'center',
  width: '100%',
};

const title = {
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '500 !important',
  color: ADMIN_COLORS.text,
};

/**
 * Page-level loader position.
 *
 * `#root` is `transform: scale(--app-scale)` with a one-viewport height (see
 * index.css), which makes it the containing block for `position: fixed`. Pinning
 * to all four edges therefore lands the loader dead centre of the viewport both
 * horizontally and vertically, at every breakpoint — rather than at the top of
 * whatever the loading page happens to be rendering into.
 */
const loaderOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '24px',
  textAlign: 'center',
  // Nothing to click while loading; the header and rail stay usable behind it.
  pointerEvents: 'none',
  zIndex: 1100,
};

/**
 * The retreX loader — the same `assets/images/loading.png` and the `.loaderImage`
 * spin from index.css that `component/Loader` uses, so the console loads with
 * the app's own mark rather than a second loader design.
 *
 * Centred on the viewport by default. `inline` keeps it in the flow instead, for
 * the loading row inside a card or a data table.
 */
export function AdminLoadingState({ label, inline }) {
  return (
    <Box sx={inline ? wrap : loaderOverlay} role="status" aria-live="polite">
      <Box
        component="img"
        alt=""
        src={Loading}
        className="loaderImage"
        sx={{
          width: inline ? '64px' : { xs: '90px', sm: '120px' },
          height: 'auto',
          userSelect: 'none',
        }}
      />
      <Typography sx={adminHelperText}>{label}</Typography>
    </Box>
  );
}

AdminLoadingState.propTypes = { label: PropTypes.string, inline: PropTypes.bool };
AdminLoadingState.defaultProps = { label: 'Loading…', inline: false };

export function AdminEmptyState({ title: heading, description, action }) {
  return (
    <Box sx={wrap}>
      <InboxOutlinedIcon sx={{ fontSize: 38, color: '#C2C2C2' }} />
      <Typography sx={title}>{heading}</Typography>
      {description ? (
        <Typography sx={adminHelperText}>{description}</Typography>
      ) : null}
      {action}
    </Box>
  );
}

AdminEmptyState.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  action: PropTypes.node,
};
AdminEmptyState.defaultProps = {
  title: 'Nothing to show yet',
  description: '',
  action: null,
};

export function AdminErrorState({ title: heading, message, onRetry }) {
  const displayTitle = heading || message || 'Something went wrong';
  return (
    <Box sx={wrap} role="alert">
      <ErrorOutlineIcon sx={{ fontSize: 38, color: '#F94839' }} />
      <Typography sx={title}>{displayTitle}</Typography>
      {message && message !== displayTitle ? (
        <Typography sx={adminHelperText}>{message}</Typography>
      ) : null}
      {onRetry ? (
        <Button variant="outlined" sx={adminSecondaryButton} onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </Box>
  );
}


AdminErrorState.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  onRetry: PropTypes.func,
};
AdminErrorState.defaultProps = {
  title: '',
  message: 'We could not load this section. Please try again.',
  onRetry: null,
};


/** Rendered in place of a module the signed-in admin is not allowed to open. */
export function AdminAccessDenied() {
  return (
    <Box sx={{ ...wrap, padding: '80px 24px' }} role="alert">
      <LockOutlinedIcon sx={{ fontSize: 40, color: ADMIN_COLORS.accent }} />
      <Typography sx={title}>You don’t have access to this section</Typography>
      <Typography sx={adminHelperText}>
        Your admin role does not include this permission. Contact a Super Admin
        if you need it.
      </Typography>
    </Box>
  );
}

export default AdminLoadingState;
